import { type RefObject, useEffect, useRef, useState } from 'react';

import { useAppServices } from '@/app/AppServicesContext';
import type { BriefDraftFields, BriefField, BriefStep } from '@/domain/brief/BriefDraft';
import { BriefWizard } from '@/features/brief/model/BriefWizard';

export interface BriefWizardController {
  readonly wizard: BriefWizard;
  readonly update: <TField extends BriefField>(
    field: TField,
    value: BriefDraftFields[TField],
  ) => void;
  readonly touch: (field: BriefField) => void;
  readonly setHoneypot: (value: string) => void;
  readonly next: () => void;
  readonly back: () => void;
  readonly goTo: (step: BriefStep) => void;
  readonly submit: () => Promise<void>;
  readonly restart: () => void;
}

/** Titre de l'étape, ou premier problème : champ invalide, sinon premier choix. */
const STEP_HEADING = '[data-step-heading]';
const FIRST_PROBLEM = '[aria-invalid="true"], [aria-pressed]';

/**
 * Branche le modèle `BriefWizard` sur React et sur le cas d'usage `SubmitBrief`.
 * Les transitions passent par des mises à jour fonctionnelles (jamais d'état
 * périmé) ; le focus se déduit de l'état : nouvelle étape, ou tentative refusée.
 */
export function useBriefWizard(form: RefObject<HTMLFormElement | null>): BriefWizardController {
  const { submitBrief } = useAppServices();
  const [wizard, setWizard] = useState(() => BriefWizard.start());
  const previous = useRef(wizard);
  const pendingRequest = useRef<AbortController | null>(null);

  useEffect(() => {
    const before = previous.current;
    previous.current = wizard;
    const selector =
      wizard.step !== before.step
        ? STEP_HEADING
        : wizard.rejectedAttempts > before.rejectedAttempts
          ? FIRST_PROBLEM
          : null;
    if (selector)
      form.current?.querySelector<HTMLElement>(selector)?.focus({ preventScroll: true });
  }, [wizard, form]);

  useEffect(
    () => () => {
      pendingRequest.current?.abort();
    },
    [],
  );

  const apply = (action: (current: BriefWizard) => BriefWizard): void => {
    setWizard(action);
  };

  const submit = async (): Promise<void> => {
    // L'envoi part de l'état affiché au moment du clic.
    const attempt = wizard.beginSubmit();
    setWizard(attempt);
    if (attempt.status !== 'submitting') return;

    pendingRequest.current?.abort();
    const request = new AbortController();
    pendingRequest.current = request;
    try {
      const result = await submitBrief.execute({
        draft: attempt.draft,
        honeypot: attempt.honeypot,
        signal: request.signal,
      });
      apply((current) => (result.ok ? current.succeed(result.value) : current.fail(result.error)));
    } catch {
      // Envoi annulé parce que le formulaire a disparu : rien à faire.
    }
  };

  return {
    wizard,
    update: (field, value) => {
      apply((current) => current.update(field, value));
    },
    touch: (field) => {
      apply((current) => current.touch(field));
    },
    setHoneypot: (value) => {
      apply((current) => current.setHoneypot(value));
    },
    next: () => {
      apply((current) => current.next());
    },
    back: () => {
      apply((current) => current.back());
    },
    goTo: (step) => {
      apply((current) => current.goTo(step));
    },
    submit,
    restart: () => {
      setWizard(BriefWizard.start());
    },
  };
}
